/**
 * Simple 3D mesh geometries for vehicle types
 * These are placeholder meshes - replace with proper OBJ models later
 */

export type VehicleIconType = 'metro' | 'bus' | 'tram' | 'train' | 'ferry' | 'default';

export interface MeshGeometry {
  positions: { value: Float32Array; size: number };
  normals: { value: Float32Array; size: number };
  indices?: { value: Uint16Array; size: number };
  [key: string]: any;
}

// Simple box mesh (for buses)
const createBox = (width: number, height: number, depth: number): MeshGeometry => {
  const w = width / 2;
  const h = height / 2;
  const d = depth / 2;

  const positions = new Float32Array([
    // Front face
    -w, -h, d,  w, -h, d,  w, h, d,  -w, h, d,
    // Back face
    -w, -h, -d,  -w, h, -d,  w, h, -d,  w, -h, -d,
    // Top face
    -w, h, -d,  -w, h, d,  w, h, d,  w, h, -d,
    // Bottom face
    -w, -h, -d,  w, -h, -d,  w, -h, d,  -w, -h, d,
    // Right face
    w, -h, -d,  w, h, -d,  w, h, d,  w, -h, d,
    // Left face
    -w, -h, -d,  -w, -h, d,  -w, h, d,  -w, h, -d
  ]);

  const normals = new Float32Array([
    // Front
    0, 0, 1,  0, 0, 1,  0, 0, 1,  0, 0, 1,
    // Back
    0, 0, -1,  0, 0, -1,  0, 0, -1,  0, 0, -1,
    // Top
    0, 1, 0,  0, 1, 0,  0, 1, 0,  0, 1, 0,
    // Bottom
    0, -1, 0,  0, -1, 0,  0, -1, 0,  0, -1, 0,
    // Right
    1, 0, 0,  1, 0, 0,  1, 0, 0,  1, 0, 0,
    // Left
    -1, 0, 0,  -1, 0, 0,  -1, 0, 0,  -1, 0, 0
  ]);

  const indices = new Uint16Array([
    0, 1, 2,  0, 2, 3,    // Front
    4, 5, 6,  4, 6, 7,    // Back
    8, 9, 10,  8, 10, 11, // Top
    12, 13, 14,  12, 14, 15, // Bottom
    16, 17, 18,  16, 18, 19, // Right
    20, 21, 22,  20, 22, 23  // Left
  ]);

  return {
    positions: { value: positions, size: 3 },
    normals: { value: normals, size: 3 },
    indices: { value: indices, size: 1 }
  };
};

// Arrow/wedge shape (for direction indicator)
const createArrow = (): MeshGeometry => {
  const positions = new Float32Array([
    // Arrow point (front)
    0, 0, 1.5,
    // Arrow base corners
    -0.5, -0.5, -0.5,
    0.5, -0.5, -0.5,
    0.5, 0.5, -0.5,
    -0.5, 0.5, -0.5,
    // Base face center
    0, 0, -0.5
  ]);

  const normals = new Float32Array([
    0, 0, 1,
    0, -1, 0,
    1, 0, 0,
    0, 1, 0,
    -1, 0, 0,
    0, 0, -1
  ]);

  const indices = new Uint16Array([
    // Front triangular faces
    0, 1, 2,  // Bottom front
    0, 2, 3,  // Right front
    0, 3, 4,  // Top front
    0, 4, 1,  // Left front
    // Base
    5, 2, 1,
    5, 3, 2,
    5, 4, 3,
    5, 1, 4
  ]);

  return {
    positions: { value: positions, size: 3 },
    normals: { value: normals, size: 3 },
    indices: { value: indices, size: 1 }
  };
};

// Cylinder approximation (for simple shapes)
const createCylinder = (radius: number, height: number, segments = 8): MeshGeometry => {
  const positionsArray: number[] = [];
  const normalsArray: number[] = [];
  const indicesArray: number[] = [];

  // Top and bottom centers
  positionsArray.push(0, height / 2, 0);
  normalsArray.push(0, 1, 0);
  positionsArray.push(0, -height / 2, 0);
  normalsArray.push(0, -1, 0);

  // Side vertices
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;

    // Top
    positionsArray.push(x, height / 2, z);
    normalsArray.push(0, 1, 0);

    // Bottom
    positionsArray.push(x, -height / 2, z);
    normalsArray.push(0, -1, 0);

    // Side normals
    positionsArray.push(x, height / 2, z);
    normalsArray.push(Math.cos(angle), 0, Math.sin(angle));
    positionsArray.push(x, -height / 2, z);
    normalsArray.push(Math.cos(angle), 0, Math.sin(angle));
  }

  // Build indices
  for (let i = 0; i < segments; i++) {
    const base = 2 + i * 4;

    // Top cap
    indicesArray.push(0, base, base + 4);

    // Bottom cap
    indicesArray.push(1, base + 5, base + 1);

    // Side faces
    indicesArray.push(base + 2, base + 3, base + 7);
    indicesArray.push(base + 2, base + 7, base + 6);
  }

  return {
    positions: { value: new Float32Array(positionsArray), size: 3 },
    normals: { value: new Float32Array(normalsArray), size: 3 },
    indices: { value: new Uint16Array(indicesArray), size: 1 }
  };
};

// Predefined meshes for each vehicle type
export const VEHICLE_MESHES: Record<VehicleIconType, MeshGeometry> = {
  metro: createBox(1.5, 1.2, 2.5),      // Long rectangular box
  bus: createBox(1.3, 1.5, 2.0),        // Tall box
  tram: createBox(1.2, 1.0, 2.8),       // Low, long box
  train: createBox(1.5, 1.3, 3.5),      // Very long box
  ferry: createBox(2.0, 1.0, 2.5),      // Wide, flat box
  default: createCylinder(0.8, 1.5, 8)  // Simple cylinder
};

// Arrow mesh for direction indicator (optional overlay)
export const DIRECTION_ARROW = createArrow();

// Get mesh for a vehicle type
export function getVehicleMesh(vehicleType: string): MeshGeometry {
  return VEHICLE_MESHES[vehicleType as VehicleIconType] || VEHICLE_MESHES.default;
}

// Scale factors for different vehicle types
export const VEHICLE_MESH_SCALES: Record<VehicleIconType, number> = {
  metro: 1.4,
  bus: 1.1,
  tram: 1.2,
  train: 1.5,
  ferry: 1.4,
  default: 1
};

export function getVehicleMeshScale(vehicleType: string): number {
  return VEHICLE_MESH_SCALES[vehicleType as VehicleIconType] || VEHICLE_MESH_SCALES.default;
}
